import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Link, useNavigate } from "react-router-dom";
import { client } from "@/utils/AxiosClient";
import { Bell } from "@phosphor-icons/react";
import {
  getNotifications,
  getUnreadNotificationsCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "@/utils/Services";
import { Notification } from "@/utils/Types";
import InfiniteScroll from "react-infinite-scroll-component";

import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  FileText,
  Gear,
  Sparkle,
  NotePencil,
  Check,
} from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  SortDescending,
  SortAscending,
  FunnelX,
  Funnel,
  // FileText,
  Stethoscope,
  Heartbeat,
  Thermometer,
  Brain,
  Users,
  Syringe,
  FirstAidKit,
  Pill,
  FunnelSimple,
} from "@phosphor-icons/react";

const SOCKET_AUTH_URL = import.meta.env.VITE_SOCKET_AUTH_URL as string;
const NOTIF_SOCKET_URL = import.meta.env.VITE_NOTIF_SOCKET_URL as string;

const SOCKET_STATUS_CONNECTED = "Connected";
const SOCKET_STATUS_UPDATED = "updated";
const SOCKET_STATUS_USER_UPDATED = "user_updated";

const options = {
  history_present_illness: {
    text: "History of Present Illness",
    icon: <FileText size={20} />,
  },
  review_of_systems: {
    text: "Review of Systems",
    icon: <Thermometer size={20} />,
  },
  physical_exam: {
    text: "Physical Exam",
    icon: <Stethoscope size={20} />,
  },
  differential_diagnosis: {
    text: "Differential Diagnosis",
    icon: <Heartbeat size={20} />,
  },
  medical_decision_making: {
    text: "Medical Decision Making",
    icon: <Brain size={20} />,
  },
  consults: {
    text: "Consults",
    icon: <Users size={20} />,
  },
  procedures: {
    text: "Procedures",
    icon: <Syringe size={20} />,
  },
  critical_care_note: {
    text: "Critical Care Note",
    icon: <FirstAidKit size={20} />,
  },
  disposition: {
    text: "Disposition",
    icon: <Pill size={20} />,
  },
};

const initialNotifications = [
  {
    id: 1,
    title: "New template available",
    message: "A new SOAP note template has been added to your library.",
    time: "2h ago",
    read: false,
  },
  {
    id: 2,
    title: "Payment successful",
    message: "Your subscription has been renewed successfully.",
    time: "1d ago",
    read: false,
  },
  {
    id: 3,
    title: "System update",
    message: "We've added new features to improve your experience.",
    time: "3d ago",
    read: false,
  },
];

export function NotificationsDropdown() {
  const [limit, setLimit] = useState(20);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [notifications, setNotifications] = useState<Array<Notification>>([]);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [offset, setOffset] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const navigate = useNavigate();

  // const  = notifications.filter((n) => n.unread).length;

  const fetchMoreNotifications = () => {
    try {
      setOffset(offset + limit);
      fetchNotifications(offset + limit);
    } catch (error) {}
  };

  const fetchNotifications = async (off = 0) => {
    try {
      let response: {
        count: number;
        results: Notification[];
      };

      response = await getNotifications({
        limit: limit,
        offset: off,
      });

      setNotifications((prev) => [...prev, ...response?.results]);
      setNotificationsCount(response?.count);
    } catch (error) {}
  };

  useEffect(() => {
    const res = setTimeout(async () => {
      fetchNotifications(0);
      fetchUnreadNotificationsCount();
    });

    return () => {
      clearTimeout(res);
    };
  }, []);

  useEffect(() => {
    const openSocket = async () => {
      try {
        const {
          data: { uuid: uuidToken },
        } = await client.post(SOCKET_AUTH_URL);

        const newSocket = new WebSocket(
          `${NOTIF_SOCKET_URL}?uuid=${uuidToken}`
        );

        newSocket.onerror = function (event) {
          console.error("WebSocket error observed:", event);
        };

        newSocket.onclose = function (event) {
          if (event.wasClean) {
          } else {
          }
        };

        newSocket.onopen = () => {
          setSocket(newSocket);
        };
      } catch (error: any) {
        if (error.response) {
        } else if (error.request) {
        } else {
        }
        console.error("Error opening WebSocket:", error);
      }
    };

    if (!socket) {
      openSocket();
    }

    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [socket]);

  useEffect(() => {
    if (socket) {
      socket.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);

          setNotifications((prev) => [message.notification, ...prev]);
          fetchUnreadNotificationsCount();
        } catch (error) {
          console.error("Error while parsing socket message:", error);
        }
      };
    }
  }, [socket]);

  const fetchUnreadNotificationsCount = async () => {
    const res = await getUnreadNotificationsCount({});

    setUnreadCount(res.unread_count);
  };

  const markAllAsRead = async () => {
    const res = await markAllNotificationsAsRead();

    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
  };

  const markNotifAsRead = async (notif: Notification) => {
    const res = await markNotificationAsRead(notif.slug);

    setNotifications(
      notifications.map((notf) =>
        notf.slug === notif.slug ? { ...notf, unread: false } : notf
      )
    );
    setUnreadCount(unreadCount - 1);

    navigate(
      `/dashboard/shared-templates/${notif.action_object?.public_access_key}`
    );
  };

  const notify = (notif: Notification) => {
    return (
      <>
        {notif.verb === "shared_template" && (
          <>
            <div
              onClick={() => {
                markNotifAsRead(notif);
              }}
            >
              <div className="flex w-full space-x-3">
                <div className="flex items-center justify-center h-8 w-8">
                  {
                    // @ts-ignore
                    options[notif?.action_object?.category!]?.icon || (
                      <FileText size={20} />
                    )
                  }
                </div>

                <div className="flex flex-wrap items-center">
                  <div>
                    <p className="text-xs">
                      <span className="font-bold">
                        {notif.actor.first_name} {notif.actor.last_name}
                      </span>
                      <span className="mx-1">shared a template with you:</span>
                      <span className="font-bold">
                        {notif.action_object?.title}
                      </span>
                    </p>
                  </div>
                  <div className="mt-auto ml-auto text-xs">
                    {notif?.natural_time}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="flex items-center justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative p-2">
            <Bell
              size={25}
              weight={unreadCount > 0 ? "fill" : "regular"}
              className={unreadCount > 0 ? "text-sky-200 dark:text-sky-50" : ""}
            />
            {unreadCount > 0 && (
              <span className="z-10 absolute -top-1 -right-[2px] h-4 w-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-80 dark:bg-gray-950"
          align="end"
          forceMount
        >
          <DropdownMenuLabel className="font-normal">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium leading-none">Notifications</p>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
              >
                Mark all as read
              </Button>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div
            id="scrollableNotifications"
            className="max-h-80 overflow-y-auto"
          >
            {!notifications.length ? (
              <div className="p-3 text-center text-xs text-muted-foreground">
                No notifications
              </div>
            ) : (
              <InfiniteScroll
                dataLength={notifications.length}
                next={fetchMoreNotifications}
                hasMore={notificationsCount > notifications.length}
                loader={
                  <div className="w-full text-center p-2">
                    <span className="loader w-5 h-5"></span>
                  </div>
                }
                scrollableTarget="scrollableNotifications"
              >
                {notifications.map((notification) => (
                  <DropdownMenuItem
                    key={notification?.id}
                    className={`p-3 cursor-pointer ${
                      notification.unread ? "" : "opacity-60"
                    }`}
                  >
                    {notify(notification)}
                  </DropdownMenuItem>
                ))}
              </InfiniteScroll>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
